import React, { useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  VStack,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useUpdateSeatingPlan } from 'queries/seatingPlan.query'
import { useGetVenues } from 'queries/venue.query'

export function EditPlanModal({ isOpen, onClose, plan }) {
  const inputColor = useColorModeValue('gray.800', 'white')
  const toast = useToast()
  const updatePlanMutation = useUpdateSeatingPlan()
  const { data: venuesData } = useGetVenues(1, 100)

  const venues = React.useMemo(() => {
    if (venuesData?.data) return venuesData.data
    if (Array.isArray(venuesData)) return venuesData
    return []
  }, [venuesData])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      venue_id: '',
    },
  })

  useEffect(() => {
    if (plan && isOpen) {
      reset({
        name: plan.name || '',
        venue_id: plan.venue_id || plan.venue?.id || '',
      })
    }
  }, [plan, isOpen, reset])

  const onSubmit = async (formData) => {
    if (!plan?.id) return

    try {
      await updatePlanMutation.mutateAsync({
        id: plan.id,
        data: {
          name: formData.name.trim(),
          venue_id: formData.venue_id ? Number(formData.venue_id) : null,
        },
      })

      toast({
        title: 'Seating plan updated',
        description: `"${formData.name}" details updated successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      onClose()
    } catch (err) {
      toast({
        title: 'Failed to update plan',
        description: err?.response?.data?.message || err?.message || 'Something went wrong',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>Edit Seating Plan Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={!!errors.name}>
              <FormLabel>Plan Title</FormLabel>
              <Input
                color={inputColor}
                placeholder="e.g. Dietzenbach Hauptsaal - Concert Setup"
                {...register('name', {
                  required: 'Plan title is required',
                  maxLength: {
                    value: 255,
                    message: 'Plan title must not exceed 255 characters',
                  },
                })}
              />
              {errors.name && (
                <FormErrorMessage>{errors.name.message}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Associated Venue</FormLabel>
              <Select
                placeholder="Select a venue (Optional)..."
                color={inputColor}
                {...register('venue_id')}
              >
                {venues.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.city})
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            type="submit"
            isLoading={isSubmitting || updatePlanMutation.isPending}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
